package dev.youndie.site.pages

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.*
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.layout.NoLayout
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobwebx.frontmatter.*
import com.varabyte.kobwebx.markdown.*

@InitRoute
fun initKvadrantUiPage(ctx: InitRouteContext) {
    ctx.data.add(MarkdownContext("KvadrantUi.md", FrontMatterElement.Builder { addScalar("title", "kvadrant-ui"); addScalar("searchTitle", "Windows Phone Metro components for Compose Multiplatform"); addScalar("description", "A Metro component library for Compose Multiplatform where every number comes from a named Microsoft document, and the eight that have no public source ship as parameters saying so."); addScalar("library", "kvadrant-ui"); addScalar("post", "/blog/metro-for-compose-multiplatform"); addScalar("docs", "https://youndie.github.io/kvadrant-ui/"); }))
}

@Page("/kvadrant-ui")
@Layout(".components.layouts.LibraryLayout")
@Composable
fun KvadrantUiPage() {
    org.jetbrains.compose.web.dom.P {
        com.varabyte.kobweb.silk.components.graphics.Image("https://s3.kotlin.website/public/kvadrant-ui/start.png", "The Start screen: live tiles that flip, cycle and carry a badge, under a pivot and an application bar")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("why-it-exists") }) {
        org.jetbrains.compose.web.dom.Text("Why it exists")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Metro reimplementations are usually built from memory and screenshots, and it shows in the places memory is wrong. The Windows Phone SDK's design-time ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("System.Windows.xaml")
        }
        org.jetbrains.compose.web.dom.Text(" settles a series of things that looking at pictures gets backwards: a text box is a ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("light")
        }
        org.jetbrains.compose.web.dom.Text(" field in both themes, focusing it does not bring in the accent colour, and a button's visible border sits inside twelve pixels of invisible touch target.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("So every colour, every metric and every motion curve here is generated from a vendored dump of Microsoft's own theme resources rather than typed in — and where no public source exists, the KDoc says so and the number ships as a ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("parameter")
        }
        org.jetbrains.compose.web.dom.Text(" rather than a constant.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("anything-better-than-the-phone-is-behind-a-flag") }) {
        org.jetbrains.compose.web.dom.Text("Anything better than the phone is behind a flag")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("KvadrantTheme(remastered = true)")
        }
        org.jetbrains.compose.web.dom.Text(" is off by default, and every deviation it enables is a row in the research document saying what it replaces.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("With it off, a component that looks ")
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("nicer")
        }
        org.jetbrains.compose.web.dom.Text(" than Windows Phone is a ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("defect")
        }
        org.jetbrains.compose.web.dom.Text(". That is what keeps the fidelity claim falsifiable: otherwise you can no longer tell a faithful component from a pleasant one by looking. Restoring behaviour the original had is not a deviation and is not gated.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("the-press-feedback-is-the-theme-s-not-the-call-site-s") }) {
        org.jetbrains.compose.web.dom.Text("The press feedback is the theme's, not the call site's")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-kotlin") }) { org.jetbrains.compose.web.dom.Text("""KvadrantTheme(
    colors = KvadrantColors.dark(),
    typography = KvadrantTypography.default(kvadrantLatin()),
) {
    KvadrantPage(applicationTitle = "KVADRANT UI", pageTitle = "inbox") {
        KvadrantListItem("Anna Peterson", subtitle = "meeting on Thursday", onClick = {})
        KvadrantButton("reply", onClick = {})
    }
}
""") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("KvadrantTheme")
        }
        org.jetbrains.compose.web.dom.Text(" replaces ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("LocalIndication")
        }
        org.jetbrains.compose.web.dom.Text(" with the tilt, so anything clickable underneath it leans towards the finger without being asked to — and it replaces ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("LocalOverscrollFactory")
        }
        org.jetbrains.compose.web.dom.Text(" too, because a Metro list neither ripples nor stretches at its end.")
    }
    org.jetbrains.compose.web.dom.P {
        com.varabyte.kobweb.silk.components.graphics.Image("https://s3.kotlin.website/public/kvadrant-ui/controls.png", "Text boxes, a button, two toggle switches, list rows and tiles, in the dark theme")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("the-light-theme-is-not-an-inversion-of-the-dark-one") }) {
        org.jetbrains.compose.web.dom.Text("The light theme is not an inversion of the dark one")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Both are transcribed; neither is derived. The twenty Windows Phone accents ship with their contrast ratios measured, beside accessible variants for the callers who need them — opt-in, because replacing the phone's palette by default would be the same mistake as improving its components by default.")
    }
    org.jetbrains.compose.web.dom.P {
        com.varabyte.kobweb.silk.components.graphics.Image("https://s3.kotlin.website/public/kvadrant-ui/accents.png", "The twenty Windows Phone accents with their contrast ratios, beside the accessible variants")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("no-segoe-file-is-in-the-repository") }) {
        org.jetbrains.compose.web.dom.Text("No Segoe file is in the repository")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Selawik is Microsoft's own metric-compatible stand-in and is the reason a typeface can ship here at all. It has no Cyrillic, so Source Sans 3 is bundled as the companion and ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("KvadrantText")
        }
        org.jetbrains.compose.web.dom.Text(" routes ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("per character")
        }
        org.jetbrains.compose.web.dom.Text(" — a ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("FontFamily")
        }
        org.jetbrains.compose.web.dom.Text(" list picks weight and style variants and does not fall back on a missing glyph, which was measured rather than assumed.")
    }
    org.jetbrains.compose.web.dom.P {
        com.varabyte.kobweb.silk.components.graphics.Image("https://s3.kotlin.website/public/kvadrant-ui/icons.png", "Forty drawn icons")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The forty icons are drawn, for the same reason: Segoe's glyph set is not redistributable, and a placeholder set would have been the one part of this library that was invented.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("install") }) {
        org.jetbrains.compose.web.dom.Text("Install")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-kotlin") }) { org.jetbrains.compose.web.dom.Text("""repositories {
    maven("https://reposilite.kotlin.website/snapshots") {
        mavenContent { includeGroupAndSubgroups("io.github.youndie") }
    }
}

dependencies {
    implementation("io.github.youndie:kvadrant-core:0.1.0")
    // Only if the application also uses Material 3.
    implementation("io.github.youndie:kvadrant-material-adapter:0.1.0")
}
""") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("kvadrant-core")
        }
        org.jetbrains.compose.web.dom.Text(" publishes desktop, Android, wasm and both iOS simulator and device targets; the adapter publishes the first three and ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("has no iOS variant")
        }
        org.jetbrains.compose.web.dom.Text(".")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("what-it-is-not") }) {
        org.jetbrains.compose.web.dom.Text("What it is not")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The screenshot suite renders Cyrillic differently under FreeType than under macOS, so it runs on macOS and a golden recorded elsewhere is a different claim. Only the SemiLight weight of the Cyrillic companion is calibrated against the render; the other four Metro weights are not. And ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("wasmJsBrowserTest")
        }
        org.jetbrains.compose.web.dom.Text(" is skipped, which in a green build reads exactly like a pass — which is why it says so here.")
    }
}
